// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Play, Search, DollarSign, Lock } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

interface VideoPackageData {
  slug: string;
  name: string;
  description: string;
  price_cents: number;
  thumbnail_url: string;
  route: string;
}

const VideoMarketplace: React.FC = () => {
  const [videoPackages, setVideoPackages] = useState<VideoPackageData[]>([]);
  const [filteredPackages, setFilteredPackages] = useState<VideoPackageData[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [priceFilter, setPriceFilter] = useState<'all' | 'low' | 'medium' | 'high'>('all');
  const navigate = useNavigate();

  useEffect(() => {
    fetchVideoMemberships();
  }, []);

  const fetchVideoMemberships = async () => {
    try {
      console.log('Fetching video memberships...');
      const { data, error } = await supabase
        .from('memberships')
        .select('*')
        .like('slug', 'video%')
        .eq('is_active', true)
        .eq('is_addon', true);

      console.log('Query result:', { data, error });

      if (error) throw error;

      const packages: VideoPackageData[] = data.map(membership => ({
        slug: membership.slug,
        name: membership.name,
        description: membership.description || 'Premium video content',
        price_cents: membership.price_cents || 0,
        thumbnail_url: '/placeholder.svg',
        route: `/videos/${membership.slug.replace('video_', '')}`
      }));

      console.log('Processed packages:', packages);
      setVideoPackages(packages);
      setFilteredPackages(packages);
    } catch (error) {
      console.error('Error fetching video memberships:', error);
      toast.error('Failed to load video packages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    filterPackages();
  }, [searchTerm, priceFilter, videoPackages]);

  const filterPackages = () => {
    let filtered = videoPackages;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(
        pkg =>
          pkg.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          pkg.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Price filter
    if (priceFilter !== 'all') {
      filtered = filtered.filter(pkg => {
        const price = pkg.price_cents / 100;
        switch (priceFilter) {
          case 'low':
            return price < 75;
          case 'medium':
            return price >= 75 && price < 150;
          case 'high':
            return price >= 150;
          default:
            return true;
        }
      });
    }

    setFilteredPackages(filtered);
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price);
  };

  const formatPriceCents = (cents: number) => {
    return formatPrice(cents / 100);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full mx-auto"></div>
            <p className="mt-4 text-muted-foreground">Loading video events...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-primary text-primary-foreground py-16">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl font-bold mb-4">Video Marketplace</h1>
          <p className="text-xl opacity-90 max-w-2xl mx-auto">
            Access exclusive game footage, highlights, and tournament coverage from top sporting events
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Search and Filters */}
        <div className="mb-8 flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-5 w-5" />
            <Input
              placeholder="Search events..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="flex gap-2">
            <Button
              variant={priceFilter === 'all' ? 'default' : 'outline'}
              onClick={() => setPriceFilter('all')}
              size="sm"
            >
              All Prices
            </Button>
            <Button
              variant={priceFilter === 'low' ? 'default' : 'outline'}
              onClick={() => setPriceFilter('low')}
              size="sm"
            >
              Under $75
            </Button>
            <Button
              variant={priceFilter === 'medium' ? 'default' : 'outline'}
              onClick={() => setPriceFilter('medium')}
              size="sm"
            >
              $75 - $150
            </Button>
            <Button
              variant={priceFilter === 'high' ? 'default' : 'outline'}
              onClick={() => setPriceFilter('high')}
              size="sm"
            >
              $150+
            </Button>
          </div>
        </div>

        {/* Video Packages Grid */}
        {filteredPackages.length === 0 ? (
          <div className="text-center py-12">
            <Play className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">No Video Packages Found</h3>
            <p className="text-muted-foreground">
              {searchTerm || priceFilter !== 'all'
                ? 'Try adjusting your search or filters'
                : 'No video packages are currently available'}
            </p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredPackages.map((pkg) => (
              <Card key={pkg.slug} className="overflow-hidden hover:shadow-lg transition-shadow">
                <div className="aspect-video relative">
                  <img
                    src={pkg.thumbnail_url}
                    alt={pkg.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                    <Play className="h-12 w-12 text-white" />
                  </div>
                  <div className="absolute top-4 right-4">
                    <Badge variant="secondary" className="bg-blue-500 text-white">
                      <Lock className="h-3 w-3 mr-1" />
                      Premium
                    </Badge>
                  </div>
                </div>
                
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">{pkg.name}</CardTitle>
                </CardHeader>
                
                <CardContent className="pt-0">
                  <p className="text-muted-foreground text-sm mb-4 line-clamp-2">
                    {pkg.description}
                  </p>
                  
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center text-primary font-semibold">
                      <DollarSign className="h-4 w-4" />
                      {formatPriceCents(pkg.price_cents)}
                    </div>
                    <Badge variant="outline">
                      Addon Membership
                    </Badge>
                  </div>
                  
                  <Button
                    onClick={() => navigate(pkg.route)}
                    className="w-full"
                  >
                    View Details
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoMarketplace;